import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {variableChipSx} from "../../../variables/variableChipStyles";
import {FLOATING_SHADOW} from "../../shared/floatingShadow";
import VariableField from "../../shared/VariableField";
import type {PickerItem} from "../pickerItem";
import type {InfoCardState} from "./useInfoCard";

/*
    Hoisted, because MUI compares `modifiers` by identity: an inline array is a new popper on every
    keystroke, and a popper that is destroyed and recreated restores the coordinates it was built
    with before it measures again.
 */
const INFO_MODIFIERS = [
    {name: "offset", options: {offset: [-4, 8]}},
    {
        name: "flip",
        options: {
            fallbackPlacements: ["left-start", "right-end", "left-end"],
            rootBoundary: "viewport",
            padding: 8,
        },
    },
    {name: "preventOverflow", options: {rootBoundary: "viewport", padding: 8}},
];

/** Viewport coordinates, so the card does not inherit the list's scroll offset. */
const INFO_POPPER_OPTIONS = {strategy: "fixed" as const};

/** What the row stands for: its chip, what it means, an example and where the value comes from. */
export function VariableInfoCardBody({item}: {item: PickerItem}) {
    const {t} = useTranslation();
    const hasFields = item.example !== undefined || item.source !== undefined;

    return (
        <Stack gap={1}>
            <Box>
                <Box component="span" sx={variableChipSx}>{item.chipText ?? item.key}</Box>
            </Box>

            {!!item.description && <Typography variant="body2">{item.description}</Typography>}

            {hasFields && (
                <Stack gap={0.25}>
                    {item.example !== undefined && (
                        <VariableField label={t("Example")} labelWidth={56}>{item.example()}</VariableField>
                    )}
                    {item.source !== undefined && (
                        <VariableField label={t("Source")} labelWidth={56}>
                            {item.source ?? t("Derived")}
                        </VariableField>
                    )}
                </Stack>
            )}
        </Stack>
    );
}

interface VariableInfoCardProps<I extends PickerItem> {
    info: InfoCardState<I> | null;
    /** The panel's root: the card renders in there, not in the scrolling list. */
    container: HTMLElement | null;
    onClickAway: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

/**
 * Inside the popup element rather than the scrolling list: the suggestion counts a pointer down
 * in there as inside itself, so reading the card cannot dismiss it.
 */
export function VariableInfoCard<I extends PickerItem>({
    info,
    container,
    onClickAway,
    onMouseEnter,
    onMouseLeave,
}: VariableInfoCardProps<I>) {
    return (
        <Popper
            open={info !== null}
            anchorEl={info?.anchor}
            container={container}
            placement="right-start"
            modifiers={INFO_MODIFIERS}
            popperOptions={INFO_POPPER_OPTIONS}
            sx={{zIndex: theme => theme.zIndex.tooltip}}
        >
            <ClickAwayListener onClickAway={onClickAway}>
                <Paper
                    variant="outlined"
                    data-variable-info-card=""
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    sx={{width: 260, borderRadius: 2, boxShadow: FLOATING_SHADOW, p: 1.5, userSelect: "text"}}
                >
                    {info && <VariableInfoCardBody item={info.item}/>}
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
}

export default VariableInfoCard;
